import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, Zap, ArrowUpRight, RefreshCcw } from "lucide-react";
import { coachOnboardApi, BOOKING_TESTIDS as T } from "@/lib/bookingsApi";
import MarketplaceNav from "@/components/coachly/MarketplaceNav";

// Coach-facing self-service Stripe Connect onboarding.
// - Enter email → backend creates (or reuses) a Connect account + returns a
//   fresh Account Link URL.
// - After Stripe redirects back with ?done=1 we poll the status endpoint
//   until transfers capability flips to `active`.

export default function CoachOnboarding() {
  const [params, setParams] = useSearchParams();
  const initialEmail = params.get("email") || "";
  const done = params.get("done");
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadStatus = async (e = email) => {
    if (!e) return;
    setRefreshing(true);
    try {
      const s = await coachOnboardApi.status(e);
      setStatus(s);
    } catch (err) {
      setStatus(null);
      if (err?.response?.status !== 404) toast.error(err?.response?.data?.detail || "Failed to load status.");
    } finally { setRefreshing(false); }
  };

  useEffect(() => {
    if (initialEmail) loadStatus(initialEmail);
    // eslint-disable-next-line
  }, [initialEmail]);

  const startOnboarding = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Enter a valid email.");
    setBusy(true);
    try {
      // Save email in the URL so a refresh-after-return preserves it.
      setParams({ email });
      const r = await coachOnboardApi.start(email);
      window.location.href = r.onboarding_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start onboarding.");
      setBusy(false);
    }
  };

  const ready = status?.payments_ready;

  return (
    <div data-testid={T.onboardPage} className="bg-white min-h-screen">
      <MarketplaceNav />

      <section className="max-w-[900px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="eyebrow">§ Coach onboarding · Stripe Connect</div>
          <h1 className="mt-4 font-display font-medium text-[42px] sm:text-[60px] md:text-[76px] leading-[0.95] tracking-[-0.05em] text-slate-900 max-w-[18ch]">
            Connect your <span className="italic font-light text-[#2563EB]">Stripe.</span>
          </h1>
          <p className="mt-6 text-slate-600 text-lg max-w-[58ch] leading-relaxed">
            Once you're approved, connect a Stripe account so you can accept bookings. Coachly takes a 10% platform fee
            and Stripe pays the rest to your bank account.
          </p>
        </motion.div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="eyebrow">§ Step 1 · Verify email</div>
            <label className="mt-4 block text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Your Coachly email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={T.onboardEmail}
              placeholder="you@domain.com"
              className="mt-2 w-full h-12 rounded-xl bg-slate-50 border border-slate-200 px-4 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
              required
            />
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => loadStatus()}
                disabled={refreshing}
                className="h-11 px-5 rounded-full border border-slate-200 text-slate-700 hover:border-slate-300 text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />} Check status
              </button>
              <button
                type="button"
                onClick={startOnboarding}
                disabled={busy}
                data-testid={T.onboardStart}
                className="h-11 px-5 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" strokeWidth={2.4} />}
                Onboard on Stripe <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
              </button>
            </div>
            <p className="mt-3 text-[11px] text-slate-400">
              You'll be sent to Stripe's hosted flow. When you finish, Stripe returns here.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={T.onboardStatus}>
            <div className="eyebrow">§ Step 2 · Status</div>
            {!status ? (
              <p className="mt-4 text-[13px] text-slate-500">Enter your email and click "Check status".</p>
            ) : (
              <div className="mt-4 space-y-2 text-[14px]">
                <div><span className="text-slate-400">Coach:</span> {status.coach_name}</div>
                <div><span className="text-slate-400">Account:</span> {status.account_id || <em className="text-slate-500">not created</em>}</div>
                <div><span className="text-slate-400">Transfers:</span> {status.transfers_status || "n/a"}</div>
                <div><span className="text-slate-400">Requirements:</span> {status.requirements_status || "n/a"}</div>
                {ready ? (
                  <div data-testid={T.onboardReady} className="mt-3 inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 text-[#059669] text-[11.5px] font-semibold">
                    <Check className="w-3 h-3" strokeWidth={2.6} /> Ready to accept bookings
                  </div>
                ) : (
                  <div className="mt-3 inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11.5px] font-semibold">
                    Onboarding in progress
                  </div>
                )}
                {ready && (
                  <Link to={`/coach/${status.coach_slug}`} className="mt-4 inline-flex btn-primary h-11 px-5 rounded-full text-[13px] font-semibold items-center gap-2">
                    View your live profile <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {done && (
          <div className="mt-8 rounded-xl bg-[#10B981]/8 border border-[#10B981]/30 p-4 text-[13px] text-[#065f46]">
            Welcome back from Stripe. Click <b>Check status</b> to refresh.
          </div>
        )}
      </section>
    </div>
  );
}
